// Shared recommendation decision UI -- extracted from
// RecommendationBridgePanel.jsx (the Martian Bridge) so the exact same
// real decision routes and RBAC gates render identically whether reached
// via the Opportunities bridge panel or directly from the Recommendations
// page (docs/product/intelligence-console-ux-audit.md, Section 8/20). No
// new backend behavior -- every handler passed in by a caller still calls
// the exact same recommendationsAPI/actionPlansAPI routes
// RecommendationBridgePanel.jsx has always called; this file only holds
// the presentation plus the display-only transition mirrors used to
// decide which controls to show. The server re-validates every
// transition independently and remains the real authority.
//
// Martian never executes anything here -- approving a recommendation or
// an action plan is a human decision recorded for execution elsewhere
// (ActionPlanService's own docstring: "never calls any external
// platform"). No execution status, no fabricated outcome, no invented
// metric is ever shown.

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { StatusBadge, ReadinessBadge, ConfidenceIndicator } from "./Badges";
import { LoadingState } from "./StateViews";
import { CaretDown, CaretUp } from "@phosphor-icons/react";

// RecommendationService._ALLOWED_TRANSITIONS, mirrored here ONLY to decide
// which decision buttons to offer -- the server re-validates every
// transition independently and is the real authority; this is a display
// convenience, never a substitute for backend enforcement.
export const REC_DECISIONS = {
  ACTIVE: ["acknowledge", "approve", "reject", "dismiss"],
  ACKNOWLEDGED: ["approve", "reject", "dismiss"],
  APPROVED: ["dismiss"],
};
export const REC_DECISION_LABEL = { acknowledge: "Acknowledge", approve: "Approve", reject: "Reject", dismiss: "Dismiss" };

// A recommendation can produce an action plan only while it is still in
// one of these statuses -- ActionPlanService.detect_action_plans()'s own
// real check (rec.status not in (ACTIVE, ACKNOWLEDGED, APPROVED) yields
// zero plans), mirrored here so the button isn't offered for a
// rejected/dismissed/completed recommendation only to fail server-side.
export const CAN_GENERATE_PLAN_FROM = new Set(["ACTIVE", "ACKNOWLEDGED", "APPROVED"]);

// ActionPlanService._ALLOWED_TRANSITIONS -- same display-only mirroring.
export const PLAN_CAN_APPROVE = new Set(["READY"]);
export const PLAN_CAN_CANCEL = new Set(["APPROVED", "IN_PROGRESS", "BLOCKED"]);

export function errorDetail(err) {
  return err?.response?.data?.detail || err?.message || "Something went wrong.";
}

// The real Acknowledge/Approve/Reject/Dismiss controls for one
// recommendation -- RBAC-gated on isAdmin exactly as RecommendationService's
// own approve/reject/dismiss routes require an admin, never a browser-
// supplied role. Frontend visibility is not the security boundary: the
// backend re-checks the role on every call regardless of this gate.
export function RecommendationDecisionActions({ recommendation, isAdmin, onDecide, decidingId }) {
  const decisions = REC_DECISIONS[recommendation.status] || [];
  if (decisions.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-[var(--border)] mb-1" data-testid="recommendation-decisions">
      {decisions.map((action) => (
        <Button
          key={action}
          data-testid={`recommendation-${action}-button`}
          variant={action === "approve" ? "default" : action === "acknowledge" ? "outline" : "ghost"}
          size="sm"
          className="text-[11px] h-7"
          disabled={!isAdmin || decidingId === recommendation.id}
          onClick={() => onDecide(recommendation, action)}
        >
          {decidingId === recommendation.id ? "…" : REC_DECISION_LABEL[action]}
        </Button>
      ))}
      {!isAdmin && <p className="text-[10px] text-[var(--text-tertiary)]" data-testid="recommendation-role-note">Only an admin can decide on this recommendation.</p>}
    </div>
  );
}

// The real "turn this into an action plan" trigger plus the resulting
// real action-plan card(s) -- including their own missing_requirements
// list and approve/cancel controls.
export function RecommendationActionPlanSection({ recommendation, plans, loading, isAdmin, onDetect, detecting, onDecide, decidingId }) {
  if (loading) return <LoadingState testId="action-plans-loading" />;
  if (!plans || plans.length === 0) {
    return CAN_GENERATE_PLAN_FROM.has(recommendation.status) ? (
      <div className="mt-2">
        <Button
          data-testid="generate-action-plan-button"
          variant="outline"
          size="sm"
          className="text-[11px]"
          disabled={detecting}
          onClick={() => onDetect(recommendation)}
        >
          {detecting ? "Preparing…" : "Turn into an action plan"}
        </Button>
      </div>
    ) : null;
  }
  return (
    <div className="mt-2 space-y-2" data-testid="action-plan-list">
      {plans.map((plan) => (
        <div key={plan.id} className="rounded border border-[var(--border)] bg-[var(--surface-sunken)] p-2.5" data-testid="action-plan-card">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-[11.5px] font-semibold text-[var(--text-primary)]">{plan.title}</p>
            <StatusBadge value={plan.status} />
          </div>
          <div className="flex items-center gap-3 flex-wrap text-[10px] mb-1.5">
            <span className="text-[var(--text-secondary)]">Readiness: </span>
            <ReadinessBadge value={plan.readiness} />
            {plan.confidence !== undefined && plan.confidence !== null && (
              <div className="flex items-center gap-1"><span className="text-[var(--text-secondary)]">Confidence:</span><ConfidenceIndicator value={plan.confidence} /></div>
            )}
            {plan.risk && <span className="text-[var(--text-secondary)]">Risk: <span className="text-[var(--text-primary)] font-mono">{plan.risk}</span></span>}
          </div>
          {Array.isArray(plan.missing_requirements) && plan.missing_requirements.length > 0 && (
            <div className="mb-1.5" data-testid="action-plan-missing-requirements">
              <p className="text-[9.5px] font-mono uppercase text-[var(--text-tertiary)]">Missing before this is ready</p>
              <ul className="list-disc pl-4">
                {plan.missing_requirements.map((r, i) => <li key={i} className="text-[10.5px] text-[var(--text-secondary)]">{r}</li>)}
              </ul>
            </div>
          )}
          <p className="text-[10.5px] text-[var(--text-secondary)] leading-relaxed mb-2">{plan.action}</p>
          <div className="flex items-center gap-2 flex-wrap pt-1.5 border-t border-[var(--border)]">
            {PLAN_CAN_APPROVE.has(plan.status) && (
              <Button
                data-testid="approve-action-plan-button"
                size="sm"
                className="text-[11px] h-7"
                disabled={!isAdmin || decidingId === plan.id}
                onClick={() => onDecide(recommendation, plan, "approve")}
              >
                {decidingId === plan.id ? "Approving…" : "Approve plan"}
              </Button>
            )}
            {PLAN_CAN_CANCEL.has(plan.status) && (
              <Button
                data-testid="cancel-action-plan-button"
                variant="ghost"
                size="sm"
                className="text-[11px] h-7 text-[var(--text-tertiary)]"
                disabled={!isAdmin || decidingId === plan.id}
                onClick={() => onDecide(recommendation, plan, "cancel")}
              >
                {decidingId === plan.id ? "Cancelling…" : "Cancel plan"}
              </Button>
            )}
            {!isAdmin && (PLAN_CAN_APPROVE.has(plan.status) || PLAN_CAN_CANCEL.has(plan.status)) && (
              <p className="text-[10px] text-[var(--text-tertiary)]" data-testid="action-plan-role-note">Only an admin can decide on this plan.</p>
            )}
            {plan.status === "APPROVED" && (
              <p className="text-[10px] text-[var(--text-tertiary)]" data-testid="action-plan-no-execution-note">
                Approved for execution elsewhere -- Martian has not executed this and has no result to report yet.
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// The original self-contained, expandable recommendation card -- byte-
// identical behavior to its previous home inside RecommendationBridgePanel.jsx,
// composed from the two pieces above plus the recommendation's own
// objective/rationale/effort/risk/reversibility fields. Used by
// RecommendationBridgePanel exactly as before.
export function RecommendationDecisionCard({ recommendation, isAdmin, isEditor, onDecide, decidingId, onDetectPlan, detectingPlanFor, plans, plansLoading, onDecidePlan, decidingPlanId }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded border border-[var(--border)] bg-[var(--surface)] p-3" data-testid="recommendation-card">
      <button type="button" className="w-full flex items-start justify-between gap-2 text-left" onClick={() => setExpanded((v) => !v)} data-testid="recommendation-card-toggle">
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold text-[var(--text-primary)]">{recommendation.title || recommendation.action}</p>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <StatusBadge value={recommendation.status} />
            {recommendation.priority && <span className="text-[10px] font-mono text-[var(--text-tertiary)]">{recommendation.priority}</span>}
            {recommendation.confidence !== undefined && recommendation.confidence !== null && <ConfidenceIndicator value={recommendation.confidence} />}
          </div>
        </div>
        {expanded ? <CaretUp size={14} className="shrink-0 text-[var(--text-tertiary)] mt-1" /> : <CaretDown size={14} className="shrink-0 text-[var(--text-tertiary)] mt-1" />}
      </button>

      {expanded && (
        <div className="mt-2.5 pt-2.5 border-t border-[var(--border)]">
          {recommendation.objective && (
            <div className="mb-2">
              <p className="text-[9.5px] font-mono uppercase text-[var(--text-tertiary)]">Objective</p>
              <p className="text-[11.5px] text-[var(--text-primary)] leading-relaxed">{recommendation.objective}</p>
            </div>
          )}
          {recommendation.rationale && (
            <div className="mb-2">
              <p className="text-[9.5px] font-mono uppercase text-[var(--text-tertiary)]">Why Martian suggests this</p>
              <p className="text-[11.5px] text-[var(--text-secondary)] leading-relaxed">{recommendation.rationale}</p>
            </div>
          )}
          <div className="flex items-center gap-3 flex-wrap text-[10px] mb-2">
            {recommendation.effort && <span className="text-[var(--text-secondary)]">Effort: <span className="text-[var(--text-primary)] font-mono">{recommendation.effort}</span></span>}
            {recommendation.risk && <span className="text-[var(--text-secondary)]">Risk: <span className="text-[var(--text-primary)] font-mono">{recommendation.risk}</span></span>}
            {recommendation.reversibility && <span className="text-[var(--text-secondary)]">Reversibility: <span className="text-[var(--text-primary)] font-mono">{recommendation.reversibility}</span></span>}
          </div>

          <RecommendationDecisionActions recommendation={recommendation} isAdmin={isAdmin} onDecide={onDecide} decidingId={decidingId} />

          {CAN_GENERATE_PLAN_FROM.has(recommendation.status) && (
            <div className="pt-1">
              {!isEditor && (!plans || plans.length === 0) ? (
                <p className="text-[10px] text-[var(--text-tertiary)]" data-testid="action-plan-editor-role-note">An editor can turn this into an action plan.</p>
              ) : (
                <RecommendationActionPlanSection
                  recommendation={recommendation}
                  plans={plans}
                  loading={plansLoading}
                  isAdmin={isAdmin}
                  onDetect={onDetectPlan}
                  detecting={detectingPlanFor === recommendation.id}
                  onDecide={onDecidePlan}
                  decidingId={decidingPlanId}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
